/**
 * localStorage-backed Non-Compliance page filters (date_from / date_to / search).
 *
 * Lifetime: browser `localStorage` — persists across tab close and browser restart.
 * Keyed by tenant + user so filters stay private per user and never leak across
 * users on the same browser or across tenants.
 * Not cleared on logout or session timeout; only the per-page Clear button clears
 * the current user+tenant bucket.
 *
 * Separate from Site/Department/Unit location filters (REBUILD-393). Used so
 * Tasks ↔ NC tab switches restore the last NC date range (T-P1).
 */

export interface NcPageFilters {
  dateFrom: string | null
  dateTo: string | null
  search: string | null
}

export const NC_PAGE_FILTERS_STORAGE_PREFIX = 'ell.ncPageFilters.v1'

export const EMPTY_NC_PAGE_FILTERS: NcPageFilters = {
  dateFrom: null,
  dateTo: null,
  search: null,
}

export const ncPageFiltersStorageKey = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): string =>
  `${NC_PAGE_FILTERS_STORAGE_PREFIX}:tenant:${tenantId ?? 'unknown'}:user:${userId ?? 'unknown'}`

const normalizeText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export const isNcPageFiltersEmpty = (filters: NcPageFilters): boolean =>
  filters.dateFrom == null && filters.dateTo == null && filters.search == null

export const readNcPageFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): NcPageFilters => {
  if (tenantId == null || typeof localStorage === 'undefined') {
    return { ...EMPTY_NC_PAGE_FILTERS }
  }

  try {
    const raw = localStorage.getItem(ncPageFiltersStorageKey(tenantId, userId))
    if (!raw) return { ...EMPTY_NC_PAGE_FILTERS }

    const parsed = JSON.parse(raw) as Partial<NcPageFilters>
    return {
      dateFrom: normalizeText(parsed.dateFrom),
      dateTo: normalizeText(parsed.dateTo),
      search: normalizeText(parsed.search),
    }
  } catch {
    return { ...EMPTY_NC_PAGE_FILTERS }
  }
}

export const writeNcPageFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
  filters: NcPageFilters,
): void => {
  if (tenantId == null || typeof localStorage === 'undefined') return

  const next: NcPageFilters = {
    dateFrom: normalizeText(filters.dateFrom),
    dateTo: normalizeText(filters.dateTo),
    search: normalizeText(filters.search),
  }

  if (isNcPageFiltersEmpty(next)) {
    clearNcPageFilters(tenantId, userId)
    return
  }

  try {
    localStorage.setItem(ncPageFiltersStorageKey(tenantId, userId), JSON.stringify(next))
  } catch {
    // QuotaExceededError / SecurityError — persistence is best-effort.
  }
}

export const clearNcPageFilters = (
  tenantId: number | null | undefined,
  userId: number | null | undefined,
): void => {
  if (tenantId == null || typeof localStorage === 'undefined') return
  localStorage.removeItem(ncPageFiltersStorageKey(tenantId, userId))
}

export const extractNcPageFiltersFromUrl = (url: string): NcPageFilters => {
  const queryIndex = url.indexOf('?')
  const search = queryIndex >= 0 ? url.slice(queryIndex) : ''
  const params = new URLSearchParams(search)
  return {
    dateFrom: normalizeText(params.get('date_from')),
    dateTo: normalizeText(params.get('date_to')),
    search: normalizeText(params.get('search')),
  }
}

/** Merge NC page filters into a location query string (`?…` or empty). */
export const appendNcPageFiltersToQuery = (baseQuery: string, filters: NcPageFilters): string => {
  const params = new URLSearchParams(baseQuery.startsWith('?') ? baseQuery.slice(1) : baseQuery)
  if (filters.dateFrom) params.set('date_from', filters.dateFrom)
  if (filters.dateTo) params.set('date_to', filters.dateTo)
  if (filters.search) params.set('search', filters.search)
  const query = params.toString()
  return query ? `?${query}` : ''
}
